import React, { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetCrossClientSelection,
  selectCrossClientPageResults,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedResultIds,
  setCrossClientPageSelection,
} from '../../../store/crossClient/crossClientResults'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery } from '../../../store/crossClient/crossClient'
import { CrossClientChooseActionDropdown } from './CrossClientChooseActionDropdown'
import { ActionsDropdownItem } from '../Actions/CrossClientActionsDropdown'
import { useCustomWorkflowLauncher } from '../../../components/CustomWorkflows/useCustomWorkflowLauncher'
import { pluralize } from '../utils'
import './CrossClientSelectionBanner.scss'

export const CrossClientSelectionBanner: React.FC = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const pageResults = useAppSelector(selectCrossClientPageResults)
  const selectedIds = useAppSelector(selectCrossClientSelectedResultIds)

  const [triggerGetAllResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const checkboxRef = useRef<HTMLInputElement>(null)

  const pageIds = pageResults.map((result) => result.id)
  const selectedSet = new Set(selectedIds)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id))

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = !allPageSelected
    }
  }, [allPageSelected])

  const handleToggleHeader = () => {
    if (selectedIds.length) {
      dispatch(resetCrossClientSelection())
    } else {
      dispatch(setCrossClientPageSelection({ ids: pageIds, selected: true }))
    }
  }

  const resolveTargets = useCallback(async () => {
    if (!execution?.id) {
      return []
    }
    const response = await triggerGetAllResults({ accountId, executionId: execution.id, resultIds: selectedIds }).unwrap()
    return response.data
  }, [accountId, execution, selectedIds, triggerGetAllResults])

  const clientCountLabel = `${selectedIds.length.toLocaleString()} ${pluralize(selectedIds.length, 'client')}`
  const workflowLauncher = useCustomWorkflowLauncher({
    accountId,
    scope: 'CCQ',
    contextPreviews: {
      client_ids: (
        <div>
          <div className='paragraph-small medium mb-1'>Clients</div>
          <span className='d-inline-flex align-items-center button-small px-2 py-1 rounded-1 zocks-bg-color-gray-200 zocks-text-gray-900'>
            {clientCountLabel}
          </span>
        </div>
      ),
    },
  })

  const onRunWorkflow = async (workflowId: string) => {
    const targets = await resolveTargets()
    const clientIds = Array.from(new Set(targets.map((result) => result.clientId)))
    if (clientIds.length > 0) {
      workflowLauncher.launch(workflowId, { execution_id: execution?.id ?? '', client_ids: { client_ids: clientIds } })
    }
  }

  return (
    <div className='cross-client-selection-banner d-flex align-items-center gap-3 px-3 py-2'>
      <input
        ref={checkboxRef}
        type='checkbox'
        className='form-check-input m-0'
        aria-label='Clear selection'
        checked={selectedIds.length > 0}
        onChange={handleToggleHeader}
      />
      <span className='paragraph-small fw-bold zocks-text-gray-900'>
        {selectedIds.length.toLocaleString()} {pluralize(selectedIds.length, 'client')} selected
      </span>
      <CrossClientChooseActionDropdown
        count={selectedIds.length}
        scope='manual bulk'
        resolveTargets={resolveTargets}
        toggleVariant='borderless'
        toggleClassName='button-small fw-bold zocks-text-primary p-0 bg-transparent border-0'
        extraItems={workflowLauncher.workflows.map((workflow) => (
          <ActionsDropdownItem key={workflow.id} icon='bi-stars' label={workflow.name} onClick={() => void onRunWorkflow(workflow.id)} />
        ))}
      />
      {workflowLauncher.runModal}
    </div>
  )
}
